import { RejectType, Transition, TransitionService } from '@uirouter/core';

const APPLICATION_ID = '45f8cae8-19c5-4014-91ed-d33cd1dcba21';
const CLIENT_TOKEN   = 'pubdb3a966b6680a0f35d02905124da673e';

const DISABLED_ENDPOINTS = [ 'local', 'beta-api', 'development-api' ];

const REPLAY_ALLOWED_DOMAINS = [ 'workshopsoftware.com.au', 'workshopsoftware.com' ];

const REPLAY_ENABLED = false;

let hasStartedView = false;

function resolveEnv () : string {
  if (SERVER_ENDPOINT === 'https://staging-api.workshopsoftware.com.au') {
    return 'staging';
  }

  const disabled = DISABLED_ENDPOINTS.some(( fragment ) => SERVER_ENDPOINT.indexOf(fragment) !== -1);

  return disabled
    ? ''
    : 'production';
}

function resolveReplaySampleRate () : number {
  if (!REPLAY_ENABLED) {
    return 0;
  }

  const hostname = window.location.hostname;

  const isReplayAllowed = REPLAY_ALLOWED_DOMAINS.some(( domain ) =>
    hostname === domain || hostname.endsWith(`.${domain}`));

  return isReplayAllowed
    ? 100
    : 0;
}

function isConsoleError ( event : any ) : boolean {
  return event.type === 'error' && event.error?.source === 'console';
}

function isEuDeployment () : boolean {
  return window.location.hostname.endsWith('workshopsoftwareuk.com');
}

function startDatadogRumView ( transition : Transition ) : void {
  const w    : any    = window;
  const name : string = transition.to().name;

  if (!w.DD_RUM || !name) {
    return;
  }

  const url : string = transition.router.stateService.href(transition.to(), transition.params());

  hasStartedView = true;

  w.DD_RUM.onReady(() => {
    w.DD_RUM.startView({ name, url: url || undefined });
  });
}

export function initDatadogRum () : void {
  const env       = resolveEnv();
  const onWebHost = window.location.hostname.indexOf('workshopsoftware') !== -1;

  if (!env || !onWebHost || CLIENT_TOKEN.indexOf('pub') !== 0) {
    return;
  }

  const w : any = window;

  w.DD_RUM = w.DD_RUM || {
    q: [],
    onReady ( callback : () => void ) {
      w.DD_RUM.q.push(callback);
    }
  };

  const script = document.createElement('script');

  script.async = true;
  script.src   = 'https://www.datadoghq-browser-agent.com/ap2/v6/datadog-rum.js';

  document.head.appendChild(script);

  w.DD_RUM.onReady(() => {
    const config : any = {
      applicationId          : APPLICATION_ID,
      clientToken            : CLIENT_TOKEN,
      site                   : 'ap2.datadoghq.com',
      service                : 'workshop-client',
      env                    : env,
      allowedTracingUrls     : [ SERVER_ENDPOINT ],
      traceSampleRate        : 0,
      sessionSampleRate      : 100,
      sessionReplaySampleRate: resolveReplaySampleRate(),
      trackResources         : true,
      trackLongTasks         : true,
      trackViewsManually     : true,
      trackUserInteractions  : true,
      defaultPrivacyLevel    : 'mask-user-input',
      beforeSend             : ( event : any ) : boolean => !isConsoleError(event)
    };

    // EU (UK) data-residency: scrub query strings from event URLs and mask
    // action names in-browser (pre-egress) so user-entered data never reaches
    // the ap2 site. IP/geolocation are captured at intake and cannot be
    // suppressed from the SDK — that needs an EU relay/proxy (out of scope).
    // Replay is already off for EU via resolveReplaySampleRate().
    if (isEuDeployment()) {
      config.enablePrivacyForActionName = true;

      config.beforeSend = ( event : any ) : boolean => {
        const stripQuery = ( url : string ) : string => url
          ? url.split('?')[0]
          : url;

        if (event.view) {
          event.view.url = stripQuery(event.view.url);

          if (event.view.referrer) {
            event.view.referrer = stripQuery(event.view.referrer);
          }
        }

        if (event.resource && event.resource.url) {
          event.resource.url = stripQuery(event.resource.url);
        }

        return !isConsoleError(event);
      };
    }

    w.DD_RUM.init(config);
  });
}

export function trackDatadogRumViews ( transitionService : TransitionService ) : void {
  transitionService.onSuccess({}, ( transition : Transition ) : void => {
    startDatadogRumView(transition);
  });

  transitionService.onError({}, ( transition : Transition ) : void => {
    if (!hasStartedView && transition.error().type === RejectType.ERROR) {
      startDatadogRumView(transition);
    }
  });
}

interface DatadogRumUserContext {
  companyId?      : number | string;
  subscriptionId? : number | string;
  userId?         : number | string;
}

export function clearDatadogRumUser () : void {
  const w : any = window;

  if (!w.DD_RUM) {
    return;
  }

  w.DD_RUM.onReady(() => {
    w.DD_RUM.clearUser();
  });
}

export function setDatadogRumUser ( context : DatadogRumUserContext ) : void {
  const w : any = window;

  // EU (UK) data-residency: do not attach user, company, or subscription
  // identifiers to RUM on the EU deployment.
  if (!w.DD_RUM || window.location.hostname.endsWith('workshopsoftwareuk.com')) {
    return;
  }

  w.DD_RUM.onReady(() => {
    const user : { [ key : string ] : string } = {};

    if (context.userId != null) {
      user.id = String(context.userId);
    }

    if (context.companyId != null) {
      user.company_id = String(context.companyId);
    }

    if (context.subscriptionId != null) {
      user.subscription_id = String(context.subscriptionId);
    }

    w.DD_RUM.setUser(user);
  });
}
